'use client';

import { useState, useEffect } from 'react';
import FilterSidebar from './FilterSidebar';
import SearchBar from './SearchBar';
import SortControls from './SortControls';
import UseCaseCard from './UseCaseCard';
import ContributePrompt from './ContributePrompt';
import AccessGateModal from './AccessGateModal';

interface UseCase {
  id: string;
  title: string;
  industry: string;
  complexity: string;
  expertiseArea: string;
  description: string;
  expertName: string;
  expertTitle: string;
  validationScore: number;
  downloadCount: number;
  lastUpdated: string;
  tags: string[];
  isGated: boolean;
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

const mockUseCases: UseCase[] = [
  {
    id: 'uc-001',
    title: 'Predictive Maintenance for Industrial Equipment',
    industry: 'Manufacturing',
    complexity: 'Advanced',
    expertiseArea: 'Industrial IoT & Predictive Analytics',
    description: 'A comprehensive framework for implementing AI-driven predictive maintenance systems in manufacturing environments. Addresses sensor integration, failure pattern recognition, and maintenance scheduling optimization to reduce downtime by 40-60%.',
    expertName: 'Dr. Sarah Chen',
    expertTitle: 'Manufacturing Systems Architect',
    validationScore: 9.2,
    downloadCount: 247,
    lastUpdated: 'Updated 3 days ago',
    tags: ['IoT', 'Machine Learning', 'Time Series', 'Anomaly Detection'],
    isGated: false,
  },
  {
    id: 'uc-002',
    title: 'Real-Time Fraud Detection in Payment Processing',
    industry: 'Financial Services',
    complexity: 'Advanced',
    expertiseArea: 'Payment Systems & Risk Management',
    description: 'Structured approach to building real-time fraud detection systems for payment processors. Covers transaction pattern analysis, behavioral biometrics, and adaptive risk scoring with sub-100ms latency requirements.',
    expertName: 'Michael Rodriguez',
    expertTitle: 'Payment Security Specialist',
    validationScore: 9.5,
    downloadCount: 412,
    lastUpdated: 'Updated 1 week ago',
    tags: ['Real-Time Processing', 'Anomaly Detection', 'Risk Scoring', 'Compliance'],
    isGated: true,
  },
  {
    id: 'uc-003',
    title: 'Clinical Decision Support for Emergency Medicine',
    industry: 'Healthcare',
    complexity: 'Advanced',
    expertiseArea: 'Emergency Medicine & Clinical Informatics',
    description: 'Framework for AI-assisted clinical decision support in emergency departments. Addresses triage optimization, diagnostic assistance, and treatment protocol recommendations while maintaining regulatory compliance and clinical safety standards.',
    expertName: 'Dr. James Patterson',
    expertTitle: 'Emergency Medicine Physician',
    validationScore: 9.8,
    downloadCount: 189,
    lastUpdated: 'Updated 5 days ago',
    tags: ['Healthcare AI', 'Clinical Workflows', 'HIPAA Compliance', 'Decision Support'],
    isGated: true,
  },
  {
    id: 'uc-004',
    title: 'Supply Chain Demand Forecasting',
    industry: 'Retail',
    complexity: 'Intermediate',
    expertiseArea: 'Supply Chain Management',
    description: 'Practical framework for implementing demand forecasting systems in retail supply chains. Covers seasonal pattern recognition, promotional impact analysis, and inventory optimization across multi-location operations.',
    expertName: 'Lisa Thompson',
    expertTitle: 'Supply Chain Director',
    validationScore: 8.7,
    downloadCount: 356,
    lastUpdated: 'Updated 2 weeks ago',
    tags: ['Forecasting', 'Inventory Management', 'Time Series', 'Optimization'],
    isGated: false,
  },
  {
    id: 'uc-005',
    title: 'Automated Document Classification for Legal Discovery',
    industry: 'Legal Services',
    complexity: 'Intermediate',
    expertiseArea: 'Legal Technology & eDiscovery',
    description: 'Structured approach to building document classification systems for legal discovery processes. Addresses privilege detection, relevance scoring, and multi-language document processing with audit trail requirements.',
    expertName: 'Robert Kim',
    expertTitle: 'Legal Technology Consultant',
    validationScore: 8.9,
    downloadCount: 178,
    lastUpdated: 'Updated 1 week ago',
    tags: ['NLP', 'Document Processing', 'Classification', 'Legal Tech'],
    isGated: false,
  },
  {
    id: 'uc-006',
    title: 'Energy Consumption Optimization for Smart Buildings',
    industry: 'Real Estate',
    complexity: 'Intermediate',
    expertiseArea: 'Building Automation & Energy Management',
    description: 'Framework for implementing AI-driven energy optimization in commercial buildings. Covers HVAC control, occupancy prediction, and renewable energy integration to achieve 25-35% energy cost reduction.',
    expertName: 'Amanda Foster',
    expertTitle: 'Building Systems Engineer',
    validationScore: 8.4,
    downloadCount: 203,
    lastUpdated: 'Updated 4 days ago',
    tags: ['IoT', 'Energy Management', 'Optimization', 'Sustainability'],
    isGated: false,
  },
  {
    id: 'uc-007',
    title: 'Customer Churn Prediction for SaaS Platforms',
    industry: 'Technology',
    complexity: 'Foundational',
    expertiseArea: 'SaaS Analytics & Customer Success',
    description: 'Practical guide to building churn prediction models for SaaS businesses. Addresses usage pattern analysis, engagement scoring, and intervention timing to reduce churn by 20-30%.',
    expertName: 'David Martinez',
    expertTitle: 'Customer Success Director',
    validationScore: 8.1,
    downloadCount: 521,
    lastUpdated: 'Updated 3 weeks ago',
    tags: ['Customer Analytics', 'Churn Prediction', 'SaaS Metrics', 'Retention'],
    isGated: false,
  },
  {
    id: 'uc-008',
    title: 'Automated Quality Control in Food Manufacturing',
    industry: 'Manufacturing',
    complexity: 'Intermediate',
    expertiseArea: 'Food Safety & Quality Assurance',
    description: 'Framework for implementing computer vision-based quality control in food production lines. Covers defect detection, contamination identification, and compliance documentation with FDA requirements.',
    expertName: 'Dr. Emily Watson',
    expertTitle: 'Food Safety Specialist',
    validationScore: 9.1,
    downloadCount: 167,
    lastUpdated: 'Updated 1 week ago',
    tags: ['Computer Vision', 'Quality Control', 'Food Safety', 'Compliance'],
    isGated: true,
  },
  {
    id: 'uc-009',
    title: 'Personalized Learning Path Optimization',
    industry: 'Education',
    complexity: 'Intermediate',
    expertiseArea: 'Educational Technology & Learning Science',
    description: 'Structured approach to building adaptive learning systems for online education platforms. Addresses skill gap analysis, content sequencing, and learning outcome prediction to improve completion rates by 40-50%.',
    expertName: 'Dr. Jennifer Lee',
    expertTitle: 'Learning Science Researcher',
    validationScore: 8.6,
    downloadCount: 294,
    lastUpdated: 'Updated 2 weeks ago',
    tags: ['EdTech', 'Personalization', 'Learning Analytics', 'Adaptive Systems'],
    isGated: false,
  },
  {
    id: 'uc-010',
    title: 'Dynamic Pricing for Ride-Sharing Services',
    industry: 'Transportation',
    complexity: 'Advanced',
    expertiseArea: 'Transportation Economics & Operations',
    description: 'Comprehensive framework for implementing dynamic pricing algorithms in ride-sharing platforms. Covers demand prediction, supply optimization, and price elasticity modeling with real-time market conditions.',
    expertName: 'Marcus Johnson',
    expertTitle: 'Transportation Systems Analyst',
    validationScore: 9.3,
    downloadCount: 385,
    lastUpdated: 'Updated 5 days ago',
    tags: ['Dynamic Pricing', 'Optimization', 'Real-Time Systems', 'Economics'],
    isGated: true,
  },
  {
    id: 'uc-011',
    title: 'Sentiment Analysis for Brand Monitoring',
    industry: 'Marketing',
    complexity: 'Foundational',
    expertiseArea: 'Brand Management & Social Media Analytics',
    description: 'Practical guide to building sentiment analysis systems for brand monitoring across social media platforms. Addresses multi-platform data collection, sentiment classification, and crisis detection.',
    expertName: 'Rachel Green',
    expertTitle: 'Brand Strategy Consultant',
    validationScore: 7.9,
    downloadCount: 643,
    lastUpdated: 'Updated 1 month ago',
    tags: ['NLP', 'Sentiment Analysis', 'Social Media', 'Brand Monitoring'],
    isGated: false,
  },
  {
    id: 'uc-012',
    title: 'Automated Underwriting for Insurance Claims',
    industry: 'Insurance',
    complexity: 'Advanced',
    expertiseArea: 'Insurance Underwriting & Risk Assessment',
    description: 'Framework for implementing AI-driven underwriting systems for insurance claims processing. Covers risk assessment, fraud detection, and automated decision-making with regulatory compliance requirements.',
    expertName: 'Thomas Anderson',
    expertTitle: 'Insurance Underwriting Director',
    validationScore: 9.4,
    downloadCount: 231,
    lastUpdated: 'Updated 1 week ago',
    tags: ['Risk Assessment', 'Automation', 'Fraud Detection', 'Compliance'],
    isGated: true,
  },
];

const industries: FilterOption[] = [
  { id: 'manufacturing', label: 'Manufacturing', count: 2 },
  { id: 'financial-services', label: 'Financial Services', count: 1 },
  { id: 'healthcare', label: 'Healthcare', count: 1 },
  { id: 'retail', label: 'Retail', count: 1 },
  { id: 'legal-services', label: 'Legal Services', count: 1 },
  { id: 'real-estate', label: 'Real Estate', count: 1 },
  { id: 'technology', label: 'Technology', count: 1 },
  { id: 'education', label: 'Education', count: 1 },
  { id: 'transportation', label: 'Transportation', count: 1 },
  { id: 'marketing', label: 'Marketing', count: 1 },
  { id: 'insurance', label: 'Insurance', count: 1 },
];

const complexities: FilterOption[] = [
  { id: 'foundational', label: 'Foundational', count: 2 },
  { id: 'intermediate', label: 'Intermediate', count: 5 },
  { id: 'advanced', label: 'Advanced', count: 5 },
];

const expertiseAreas: FilterOption[] = [
  { id: 'iot-analytics', label: 'Industrial IoT & Predictive Analytics', count: 1 },
  { id: 'payment-security', label: 'Payment Systems & Risk Management', count: 1 },
  { id: 'clinical-informatics', label: 'Emergency Medicine & Clinical Informatics', count: 1 },
  { id: 'supply-chain', label: 'Supply Chain Management', count: 1 },
  { id: 'legal-tech', label: 'Legal Technology & eDiscovery', count: 1 },
  { id: 'energy-management', label: 'Building Automation & Energy Management', count: 1 },
  { id: 'customer-success', label: 'SaaS Analytics & Customer Success', count: 1 },
  { id: 'food-safety', label: 'Food Safety & Quality Assurance', count: 1 },
  { id: 'learning-science', label: 'Educational Technology & Learning Science', count: 1 },
  { id: 'transportation-ops', label: 'Transportation Economics & Operations', count: 1 },
  { id: 'brand-management', label: 'Brand Management & Social Media Analytics', count: 1 },
  { id: 'insurance-underwriting', label: 'Insurance Underwriting & Risk Assessment', count: 1 },
];

export default function UseCaseLibraryInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedComplexities, setSelectedComplexities] = useState<string[]>([]);
  const [selectedExpertiseAreas, setSelectedExpertiseAreas] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUseCaseTitle, setSelectedUseCaseTitle] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:268:6" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="268" data-component-end-line="275" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:269:8" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="269" data-component-end-line="274" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:270:10" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="270" data-component-end-line="273" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
            <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:271:12" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="271" data-component-end-line="271" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-surface%20mb-8%20w-1%2F3%22%7D" className="h-12 bg-surface mb-8 w-1/3"></div>
            <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:272:12" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="272" data-component-end-line="272" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-surface%22%7D" className="h-64 bg-surface"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleIndustryChange = (id: string) => {
    setSelectedIndustries((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleComplexityChange = (id: string) => {
    setSelectedComplexities((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleExpertiseAreaChange = (id: string) => {
    setSelectedExpertiseAreas((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const handleClearAll = () => {
    setSelectedIndustries([]);
    setSelectedComplexities([]);
    setSelectedExpertiseAreas([]);
  };

  const handleOpenModal = (title: string) => {
    setSelectedUseCaseTitle(title);
    setIsModalOpen(true);
  };

  const filteredUseCases = mockUseCases.filter((useCase) => {
    const matchesSearch =
      searchQuery === '' ||
      useCase.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      useCase.industry.toLowerCase().includes(searchQuery.toLowerCase()) ||
      useCase.expertiseArea.toLowerCase().includes(searchQuery.toLowerCase()) ||
      useCase.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesIndustry =
      selectedIndustries.length === 0 ||
      selectedIndustries.includes(
        useCase.industry.toLowerCase().replace(/\s+/g, '-')
      );

    const matchesComplexity =
      selectedComplexities.length === 0 ||
      selectedComplexities.includes(useCase.complexity.toLowerCase());

    const matchesExpertiseArea =
      selectedExpertiseAreas.length === 0 ||
      selectedExpertiseAreas.some((area) =>
        useCase.expertiseArea.toLowerCase().includes(area)
      );

    return (
      matchesSearch && matchesIndustry && matchesComplexity && matchesExpertiseArea
    );
  });

  const sortedUseCases = [...filteredUseCases].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.downloadCount - a.downloadCount;
      case 'rating':
        return b.validationScore - a.validationScore;
      case 'title':
        return a.title.localeCompare(b.title);
      case 'recent':
      default:
        return 0;
    }
  });

  return (
    <>
      <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:355:6" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="355" data-component-end-line="425" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:356:8" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="356" data-component-end-line="363" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
          <h1 data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:357:10" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="357" data-component-end-line="359" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20text-4xl%20md%3Atext-5xl%20font-semibold%20mb-4%20leading-tight%22%2C%22textContent%22%3A%22Use%20Case%20Library%22%7D" className="font-headline text-4xl md:text-5xl font-semibold mb-4 leading-tight">
            Use Case Library
          </h1>
          <p data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:360:10" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="360" data-component-end-line="362" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-muted-foreground%20max-w-3xl%20leading-relaxed%22%2C%22textContent%22%3A%22Structured%20problem%20definitions%20from%20domain%20experts%20across%20industries.%20Each%20use%20case%20represents%20a%20validated%20business%20problem%20with%20clear%20constraints%2C%20success%20criteria%2C%20and%20implementation%20considerations.%22%7D" className="font-body text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Structured problem definitions from domain experts across industries. Each use case represents a validated business problem with clear constraints, success criteria, and implementation considerations.
          </p>
        </div>

        <ContributePrompt data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:365:8" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="365" data-component-end-line="365" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="ContributePrompt" data-component-content="%7B%22elementName%22%3A%22ContributePrompt%22%7D" />

        <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:367:8" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="367" data-component-end-line="424" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-4%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:368:10" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="368" data-component-end-line="381" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-1%22%7D" className="lg:col-span-1">
            <FilterSidebar data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:369:12" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="369" data-component-end-line="380" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="FilterSidebar" data-component-content="%7B%22elementName%22%3A%22FilterSidebar%22%7D"
              industries={industries}
              complexities={complexities}
              expertiseAreas={expertiseAreas}
              selectedIndustries={selectedIndustries}
              selectedComplexities={selectedComplexities}
              selectedExpertiseAreas={selectedExpertiseAreas}
              onIndustryChange={handleIndustryChange}
              onComplexityChange={handleComplexityChange}
              onExpertiseAreaChange={handleExpertiseAreaChange}
              onClearAll={handleClearAll}
            />
          </div>

          <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:383:10" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="383" data-component-end-line="423" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-3%22%7D" className="lg:col-span-3">
            <SearchBar data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:384:12" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="384" data-component-end-line="388" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D"
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              totalResults={filteredUseCases.length}
            />

            <SortControls data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:390:12" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="390" data-component-end-line="395" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="SortControls" data-component-content="%7B%22elementName%22%3A%22SortControls%22%7D"
              sortBy={sortBy}
              onSortChange={setSortBy}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {sortedUseCases.length > 0 ? (
              <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:398:14" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="398" data-component-end-line="409" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={
                  viewMode === 'grid' ?'grid grid-cols-1 gap-6' :'space-y-6'
                }
              >
                {sortedUseCases.map((useCase) => (
                  <UseCaseCard data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:404:18" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="404" data-component-end-line="407" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="UseCaseCard" data-component-content="%7B%22elementName%22%3A%22UseCaseCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                    key={useCase.id}
                    {...useCase}
                  />
                ))}
              </div>
            ) : (
              <div data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:411:14" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="411" data-component-end-line="421" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%20border-2%20border-dashed%20border-border%22%7D" className="text-center py-16 border-2 border-dashed border-border">
                <p data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:412:16" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="412" data-component-end-line="414" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-muted-foreground%20mb-4%22%2C%22textContent%22%3A%22No%20use%20cases%20found%20matching%20your%20criteria%22%7D" className="font-body text-lg text-muted-foreground mb-4">
                  No use cases found matching your criteria
                </p>
                <button data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:415:16" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="415" data-component-end-line="420" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22font-cta%20text-sm%20font-medium%20px-6%20py-2%20bg-primary%20text-primary-foreground%20hover%3Abg-accent%20transition-colors%20duration-200%20border%20border-primary%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D"
                  onClick={handleClearAll}
                  className="font-cta text-sm font-medium px-6 py-2 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-200 border border-primary"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AccessGateModal data-component-id="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx:427:6" data-component-path="src/app/use-case-library/components/UseCaseLibraryInteractive.tsx" data-component-line="427" data-component-end-line="431" data-component-file="UseCaseLibraryInteractive.tsx" data-component-name="AccessGateModal" data-component-content="%7B%22elementName%22%3A%22AccessGateModal%22%7D"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        useCaseTitle={selectedUseCaseTitle}
      />
    </>
  );
}